import Router from "next/router";

const SESSIONS_ROUTE = "/[org]/sessions/[[...sessionPath]]";

/**
 * The new-session page (`/{org}/sessions/new`) shares the sessions catch-all
 * route pattern with session threads; report it under its own label so the
 * two chart separately in Grafana. The session segment comes from `asPath`
 * (`/acme/sessions/new` → `new`) rather than `Router.query`, which is still
 * empty when TTFB and LCP fire on a statically optimized first load.
 *
 * `null` outside a Next.js runtime (Storybook, component tests), where the
 * `next/router` singleton has no instance and its getters throw.
 */
export function reportedRoute(): string | null {
  const router = Router.router;
  if (!router) return null;
  if (router.pathname !== SESSIONS_ROUTE) return router.pathname;
  const path = router.asPath.split(/[?#]/)[0];
  const sessionSegment = path.split("/").filter(Boolean)[2];
  return sessionSegment === "new" ? "/[org]/sessions/new" : SESSIONS_ROUTE;
}

/**
 * Sent via `fetch` with `keepalive` and a form-encoded body: the
 * CORS-safelisted content type needs no preflight, which browsers often
 * cancel during unload, exactly when INP and CLS fire.
 */
export function postFrontendMetric(fields: Record<string, string>): void {
  void fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/frontend/metrics`, {
    method: "POST",
    keepalive: true,
    credentials: "include",
    body: new URLSearchParams(fields),
  }).catch(() => {});
}

/**
 * Kept apart from `useWebVitalsReporting` so components can report a vital
 * without importing `next/web-vitals`, whose bundle reads `__dirname` and
 * breaks under Vite (Playwright component tests).
 */
export function reportWebVital(
  name: string,
  value: number,
  route: string,
): void {
  postFrontendMetric({
    web_vital: name,
    web_vital_value: String(value),
    web_vital_route: route,
  });
}
